import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { omitBy } from 'lodash';

import Scrollbar from '../../../components/Scrollbar';
import { getNumberSwapBindings } from '../../../services/simSwap';
import { useDebounce } from '../../../utils/debounce';

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_SORT = 'createdAt,DESC';

const STATUS_CONFIG = {
  PENDING: { label: 'Pending', color: 'warning' },
  SUCCESS: { label: 'Success', color: 'success' },
  FAILED: { label: 'Failed', color: 'error' },
};

const toTitleCase = (value) =>
  value
    .toLowerCase()
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const normalizeStatus = (raw) => {
  const key = String(raw ?? '')
    .replace(/^BindingStatus\./, '')
    .trim()
    .toUpperCase();
  const preset = STATUS_CONFIG[key];
  if (preset) return preset;
  return { label: key ? toTitleCase(key) : 'Unknown', color: 'default' };
};

const StatusChip = ({ status }) => {
  const { label, color } = normalizeStatus(status);
  return <Chip label={label} color={color} size="small" />;
};

const COLUMNS = [
  { id: 'index', label: '#', render: (_row, idx, { page, size }) => page * size + idx + 1 },
  { id: 'msisdn', label: 'MSISDN', render: (row) => row.msisdn },
  { id: 'iccid', label: 'ICCID', render: (row) => row.iccid },
  { id: 'status', label: 'Status', render: (row) => <StatusChip status={row.status} /> },
  { id: 'reason', label: 'Reason', render: (row) => row.reason || '—' },
];

export default function UploadedSims() {
  const [msisdnValue, setMsisdnValue] = useState('');
  const [iccidValue, setIccidValue] = useState('');
  const debouncedMsisdn = useDebounce(msisdnValue, 400);
  const debouncedIccid = useDebounce(iccidValue, 400);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const params = useMemo(
    () =>
      omitBy(
        {
          msisdn: debouncedMsisdn,
          iccid: debouncedIccid,
          page,
          size: rowsPerPage,
          sort: DEFAULT_SORT,
        },
        (v) => v == null || v.toString().length < 1
      ),
    [debouncedMsisdn, debouncedIccid, page, rowsPerPage]
  );

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setLoading(true);
    setError('');

    getNumberSwapBindings(params, { signal: controller.signal })
      .then((response) => {
        if (active) setData(response.data);
      })
      .catch((err) => {
        if (!active || controller.signal.aborted) return;
        setError(err?.response?.data?.message || err?.message || 'Failed to load uploaded SIMs');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [params]);

  const handleChangePage = (_event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const rows = data?.content ?? [];
  const totalCount = data?.totalElements ?? 0;
  const renderCtx = { page, size: rowsPerPage };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Uploaded SIMs
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          All SIM swap bindings that have been uploaded. Search by MSISDN or ICCID.
        </Typography>

        <Stack direction="row" spacing={2} sx={{ my: 2 }}>
          <TextField
            label="MSISDN"
            variant="outlined"
            size="small"
            value={msisdnValue}
            onChange={(e) => {
              setMsisdnValue(e.target.value);
              setPage(0);
            }}
            placeholder="Search MSISDN"
          />
          <TextField
            label="ICCID"
            variant="outlined"
            size="small"
            value={iccidValue}
            onChange={(e) => {
              setIccidValue(e.target.value);
              setPage(0);
            }}
            placeholder="Search ICCID"
          />
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ mb: 1 }}>
            <LinearProgress />
          </Box>
        )}

        <Scrollbar>
          <TableContainer sx={{ maxHeight: 560 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {COLUMNS.map((col) => (
                    <TableCell key={col.id}>{col.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length > 0 ? (
                  rows.map((row, idx) => (
                    <TableRow key={`${row.msisdn}-${row.iccid}-${idx}`}>
                      {COLUMNS.map((col) => (
                        <TableCell key={col.id}>{col.render(row, idx, renderCtx)}</TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={COLUMNS.length} align="center">
                      {loading ? 'Loading…' : 'No uploaded SIMs found'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollbar>

        <TablePagination
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </CardContent>
    </Card>
  );
}
